import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/class-control";
import { FieldConfig, VisibleWhenCondition } from "./types";
import { fieldRegistry, isSpecialFieldType } from "./field-registry";
import { InputFile } from "@/components/ui/input-file";
import { File as FileIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface DynamicFormFieldProps {
  config: FieldConfig;
}

/**
 * Componente que renderiza dinamicamente um campo de formulário
 * baseado em sua configuração
 */
export function DynamicFormField({ config }: DynamicFormFieldProps) {
  const form = useFormContext();

  // Verificar visibilidade condicional
  const watchedValue = config.visibleWhen
    ? useWatch({ control: form.control, name: config.visibleWhen.field })
    : undefined;

  if (config.visibleWhen && !evaluateCondition(watchedValue, config.visibleWhen)) {
    return null;
  }

  // Render customizado
  if (config.type === "custom" && config.render) {
    return (
      <FormField
        control={form.control}
        name={config.name}
        render={({ field, fieldState }) => (
          <FormItem className={config.className}>
            {config.label && (
              <FormLabel>
                {config.label}
                {config.required && <span className="text-destructive ml-1">*</span>}
              </FormLabel>
            )}
            <FormControl>{config.render!(field, fieldState)}</FormControl>
            {config.helpText && <FormDescription>{config.helpText}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // Select
  if (config.type === "select") {
    return <SelectField config={config} />;
  }

  // Radio Group
  if (config.type === "radio") {
    return <RadioField config={config} />;
  }

  // Checkbox
  if (config.type === "checkbox") {
    return <CheckboxField config={config} />;
  }

  // Multi-select (placeholder - pode ser expandido)
  if (config.type === "multi-select") {
    return <MultiSelectField config={config} />;
  }

  if (config.type === "file-preview") {
    return <FilePreviewField config={config} />;
  }

  // Campos padrão via registry
  const registryEntry = fieldRegistry[config.type];
  if (!registryEntry?.component) {
    console.warn(`Componente não registrado para o tipo: ${config.type}`);
    return null;
  }

  const Component = registryEntry.component;

  return (
    <FormField
      control={form.control}
      name={config.name}
      render={({ field }) => (
        <FormItem className={config.className}>
          {config.label && (
            <FormLabel>
              {config.label}
              {config.required && <span className="text-destructive ml-1">*</span>}
            </FormLabel>
          )}
          <FormControl>
            <Component {...registryEntry.propsMapper(field, config)} />
          </FormControl>
          {config.helpText && <FormDescription>{config.helpText}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Campo File Preview
 */
function FilePreviewField({ config }: { config: FieldConfig }) {
  const form = useFormContext();
  const [previewError, setPreviewError] = useState(false);

  const previewUrl = config.fileConfig?.previewUrl || null;
  const showPreview = config.fileConfig?.showPreview !== false;
  const isPdf = previewUrl?.toLowerCase().includes(".pdf");

  const handleInvalidFile = (file: File) => {
    const maxSize = config.fileConfig?.maxSize || 0;
    const sizeLabel = maxSize ? formatBytes(maxSize) : "tamanho permitido";

    toast({
      title: "Arquivo muito grande",
      description: `O arquivo excede ${sizeLabel}.`,
      variant: "destructive",
    });

    config.onFileSelect?.(null);
  };

  return (
    <FormField
      control={form.control}
      name={config.name}
      render={({ field }) => (
        <FormItem className={config.className}>
          {config.label && (
            <FormLabel>
              {config.label}
              {config.required && <span className="text-destructive ml-1">*</span>}
            </FormLabel>
          )}
          <FormControl>
            <InputFile
              field={field}
              placeholder={config.placeholder}
              accept={config.fileConfig?.accept || config.accept}
              maxSize={config.fileConfig?.maxSize}
              disabled={config.disabled}
              onFileSelect={config.onFileSelect}
              onInvalidFile={handleInvalidFile}
            />
          </FormControl>

          {showPreview && previewUrl && (
            <div className="mt-3 rounded-md border border-[#5d5d5d] bg-[#0c1427] p-3">
              {previewError ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <FileIcon className="h-4 w-4" />
                  Preview indisponível
                </div>
              ) : (
                <div className="relative w-full overflow-hidden rounded-md">
                  {isPdf ? (
                    <iframe
                      title={`Preview ${config.label || config.name}`}
                      src={previewUrl}
                      className="h-48 w-full rounded-md"
                      onError={() => setPreviewError(true)}
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt={config.label || config.name}
                      className="h-48 w-full rounded-md object-cover"
                      onError={() => setPreviewError(true)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {config.helpText && <FormDescription>{config.helpText}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Campo Select
 */
function SelectField({ config }: { config: FieldConfig }) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={config.name}
      render={({ field }) => (
        <FormItem className={config.className}>
          {config.label && (
            <FormLabel>
              {config.label}
              {config.required && <span className="text-destructive ml-1">*</span>}
            </FormLabel>
          )}
          <Select
            value={field.value}
            onValueChange={field.onChange}
            disabled={config.disabled}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={config.placeholder || "Selecione..."} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {config.options?.map((option) => (
                <SelectItem
                  key={option.value}
                  value={String(option.value)}
                  disabled={option.disabled}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {config.helpText && <FormDescription>{config.helpText}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Campo Radio Group
 */
function RadioField({ config }: { config: FieldConfig }) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={config.name}
      render={({ field }) => (
        <FormItem className={cn("space-y-3", config.className)}>
          {config.label && (
            <FormLabel>
              {config.label}
              {config.required && <span className="text-destructive ml-1">*</span>}
            </FormLabel>
          )}
          <FormControl>
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              disabled={config.disabled}
              className="flex flex-wrap gap-4"
            >
              {config.options?.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={String(option.value)}
                    id={`${config.name}-${option.value}`}
                    disabled={option.disabled}
                  />
                  <Label
                    htmlFor={`${config.name}-${option.value}`}
                    className="text-sm text-white cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </FormControl>
          {config.helpText && <FormDescription>{config.helpText}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Campo Checkbox
 */
function CheckboxField({ config }: { config: FieldConfig }) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={config.name}
      render={({ field }) => (
        <FormItem
          className={cn(
            "flex flex-row items-start space-x-3 space-y-0",
            config.className
          )}
        >
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              id={config.name}
            />
          </FormControl>
          <div className="space-y-1 leading-none">
            {config.label && (
              <label
                htmlFor={config.name}
                className={cn(
                  "text-sm font-medium leading-none cursor-pointer text-white",
                  config.disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                {config.label}
                {config.required && <span className="text-destructive ml-1">*</span>}
              </label>
            )}
            {config.helpText && (
              <FormDescription>{config.helpText}</FormDescription>
            )}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Campo Multi-Select (implementação básica com checkboxes)
 */
function MultiSelectField({ config }: { config: FieldConfig }) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={config.name}
      render={({ field }) => {
        const selectedValues = (field.value as string[]) || [];

        const handleToggle = (value: string) => {
          const newValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
          field.onChange(newValues);
        };

        return (
          <FormItem className={config.className}>
            {config.label && (
              <FormLabel>
                {config.label}
                {config.required && <span className="text-destructive ml-1">*</span>}
              </FormLabel>
            )}
            <div className="flex flex-wrap gap-3">
              {config.options?.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    checked={selectedValues.includes(String(option.value))}
                    onCheckedChange={() => handleToggle(String(option.value))}
                    id={`${config.name}-${option.value}`}
                  />
                  <Label
                    htmlFor={`${config.name}-${option.value}`}
                    className="text-sm text-white cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {config.helpText && <FormDescription>{config.helpText}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

/**
 * Avalia a condição de visibilidade
 */
function evaluateCondition(
  value: unknown,
  condition: VisibleWhenCondition
): boolean {
  switch (condition.operator) {
    case "equals":
      return value === condition.value;

    case "notEquals":
      return value !== condition.value;

    case "contains":
      if (Array.isArray(value)) {
        return value.includes(condition.value);
      }
      return String(value).includes(String(condition.value));

    case "greaterThan":
      return Number(value) > Number(condition.value);

    case "lessThan":
      return Number(value) < Number(condition.value);

    default:
      return true;
  }
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
